import React, { useCallback, useMemo, useState } from "react";
import { GlobalModal } from "../../../../global/GlobalModal";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection
} from "../../../../global/directory";
import {
  roleNameById,
  statusNameById,
  formatDateDisplay,
  formatShiftCell,
  formatLocationCell,
  formatShiftOptionLabel,
} from "../userBuilderHelpers";
import { isProtectedUserBuilderAccount } from "../../../../public/utils/roleAccess";
import { useUserSectionContext } from "../UserSectionContext";
import UserDirectoryMemberCell from "../UserDirectoryMemberCell";
import UserAttendanceAssignModal from "../UserAttendanceAssignModal";

const USER_DIRECTORY_LIST_COLUMN_VISIBILITY_KEY =
  "girinestham.userBuilder.userDirectory.columnVisibility";

const USER_DIRECTORY_COLUMN_DEFS = [
  { id: "name", label: "Member", defaultVisible: true },
  { id: "user_code", label: "Code", defaultVisible: true },
  { id: "email", label: "Email", defaultVisible: true },
  { id: "role", label: "Role", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "phone", label: "Phone", defaultVisible: true },
  { id: "doj", label: "DOJ", defaultVisible: true },
  { id: "shift", label: "Shift", defaultVisible: true },
  { id: "location", label: "Location", defaultVisible: true },
];

function loadUserDirectoryColumnVisibility() {
  try {
    const raw = localStorage.getItem(USER_DIRECTORY_LIST_COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

function createInitialUserDirectoryColumnVisibility() {
  const saved = loadUserDirectoryColumnVisibility();
  const initial = {};
  for (const c of USER_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function userDirectoryCellValue(columnId, u, ctx) {
  const { roles, statuses } = ctx || {};
  switch (columnId) {
    case "user_code":
      return u?.user_code != null && u.user_code !== ""
        ? String(u.user_code)
        : "—";
    case "name":
      return u?.name != null && String(u.name).trim() !== ""
        ? String(u.name)
        : "—";
    case "email":
      return u?.email != null && String(u.email).trim() !== ""
        ? String(u.email)
        : "—";
    case "role": {
      if (u?.role_name) {
        const inactive = Number(u?.role_is_active) === 2 ? " (inactive)" : "";
        return `${String(u.role_name)}${inactive}`;
      }
      return roleNameById(roles, u?.role_id);
    }
    case "status":
      return statusNameById(statuses, u?.user_status_id);
    case "phone": {
      const p = u?.contact_number;
      if (p == null || p === "") return "—";
      return String(p);
    }
    case "doj":
      return formatDateDisplay(u?.date_of_joining);
    case "shift":
      return formatShiftCell(u);
    case "location":
      return formatLocationCell(u);
    default:
      return u?.[columnId] != null && u[columnId] !== ""
        ? String(u[columnId])
        : "—";
  }
}

const UserDirectorySection = () => {
  const {
    roles,
    statuses,
    attendanceShifts,
    attendanceLocations,
    draftDirectoryFilters,
    setDraftDirectoryFilters,
    clearDirectoryFilters,
    directoryError,
    listLoading,
    page,
    totalPages,
    total,
    setPage,
    rows,
    busy,
    startEdit,
    userPendingRemoval,
    removingUser,
    requestUserRemoval,
    cancelUserRemoval,
    confirmUserRemoval,
    userPendingAssignment,
    assigningUser,
    requestAttendanceAssignment,
    cancelAttendanceAssignment,
    confirmAttendanceAssignment,
    pageSize
  } = useUserSectionContext();

  const [columnVisibility, setColumnVisibility] = useState(
    createInitialUserDirectoryColumnVisibility
  );

  const valueFilterOptions = useMemo(() => {
    const col = draftDirectoryFilters.column;
    if (!col) return [];
    if (col === "role") {
      return (roles || []).map((r) => ({
        value: String(r.id),
        label: String(r.role_name ?? r.id)
      }));
    }
    if (col === "status") {
      return (statuses || []).map((s) => ({
        value: String(s.id),
        label: String(s.status_name ?? s.id)
      }));
    }
    if (col === "shift") {
      return (attendanceShifts || []).map((s) => ({
        value: String(s.id),
        label: formatShiftOptionLabel(s),
      }));
    }
    if (col === "location") {
      return (attendanceLocations || []).map((loc) => ({
        value: String(loc.id),
        label: String(loc.location_label ?? loc.id),
      }));
    }
    const seen = new Set();
    const opts = [];
    for (const row of rows || []) {
      let raw;
      let optionValue;
      if (col === "name") raw = row.name;
      else if (col === "email") raw = row.email;
      else if (col === "phone") raw = row.contact_number;
      else if (col === "user_code") raw = row.user_code;
      else if (col === "doj") {
        raw = row.date_of_joining;
        if (raw == null || raw === "") continue;
        optionValue = String(raw).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(optionValue)) continue;
        const label = formatDateDisplay(raw);
        if (!seen.has(optionValue)) {
          seen.add(optionValue);
          opts.push({ value: optionValue, label });
        }
        continue;
      } else raw = row[col];

      if (raw == null || raw === "") continue;
      optionValue = String(raw);
      const label = optionValue;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, label });
      }
    }
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base"
      })
    );
  }, [draftDirectoryFilters.column, rows, roles, statuses, attendanceShifts, attendanceLocations]);

  const getUserDirectoryExportData = useCallback(() => {
    const visibleCols = USER_DIRECTORY_COLUMN_DEFS.filter(
      (c) => columnVisibility[c.id]
    );
    const headers = visibleCols.map((c) => c.label);
    const ctx = { roles, statuses };
    const dataRows = (rows || []).map((u) =>
      visibleCols.map((c) => userDirectoryCellValue(c.id, u, ctx))
    );
    return { headers, rows: dataRows };
  }, [rows, columnVisibility, roles, statuses]);

  const visibleDataColumns = useMemo(
    () => USER_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions"
      }
    ],
    [visibleDataColumns]
  );

  const list = rows || [];
  const showRows = !listLoading && list.length > 0;
  const cellCtx = useMemo(() => ({ roles, statuses }), [roles, statuses]);

  const filters = (
    <>
      <GlobalDirectoryFiltersSection
        className="ub-filters ub-user-directory-filters"
        searchLabel="Search by user code"
        searchPlaceholder="GNWF-ABC-123456 (optional)"
        searchValue={draftDirectoryFilters.user_code_search}
        onSearchChange={(v) =>
          setDraftDirectoryFilters({
            ...draftDirectoryFilters,
            user_code_search: String(v).toUpperCase()
          })
        }
        columnLabel="Column"
        columnValue={draftDirectoryFilters.column}
        onColumnChange={(v) =>
          setDraftDirectoryFilters({
            ...draftDirectoryFilters,
            column: v,
            value: ""
          })
        }
        gridColumnDefs={USER_DIRECTORY_COLUMN_DEFS}
        columnPlaceholderOption="Select"
        valueLabel="Value"
        valueValue={draftDirectoryFilters.value}
        onValueChange={(v) =>
          setDraftDirectoryFilters({ ...draftDirectoryFilters, value: v })
        }
        valueOptions={valueFilterOptions}
        valuePlaceholderOption="All"
        onClear={clearDirectoryFilters}
        clearFilterLabel="Clear filter"
        secondaryStart={
          <GlobalDirectoryColumnPicker
            columnDefs={USER_DIRECTORY_COLUMN_DEFS}
            visibility={columnVisibility}
            onVisibilityChange={setColumnVisibility}
            storageKey={USER_DIRECTORY_LIST_COLUMN_VISIBILITY_KEY}
          />
        }
        secondaryEnd={
          <GlobalDirectoryExportMenu
            getExportData={getUserDirectoryExportData}
            filenameBase="users_directory"
          />
        }
      />
      {directoryError ? (
        <p className="ub-form-alert ub-form-alert--error" role="alert">
          {directoryError}
        </p>
      ) : null}
    </>
  );

  return (
    <div id="ub-users-directory">
      <GlobalDirectoryShell
        filters={filters}
        grid={
          <GlobalDirectoryGridSection
            loading={listLoading}
            loadingMessage="Loading users…"
            headers={tableHeaders}
            isEmpty={!listLoading && list.length === 0}
            emptyMessage="No users found."
            tableAriaLabel="Users directory table"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? list.map((u) => (
                  <tr key={u.id}>
                    {visibleDataColumns.map((col) => (
                      <td
                        key={col.id}
                        className={
                          col.id === "name"
                            ? "ub-table__col-member"
                            : col.id === "user_code"
                              ? "ub-table__col-code"
                              : undefined
                        }
                      >
                        {col.id === "name" ? (
                          <UserDirectoryMemberCell
                            photo={u.photo}
                            name={u.name}
                            userCode={u.user_code}
                          />
                        ) : (
                          userDirectoryCellValue(col.id, u, cellCtx)
                        )}
                      </td>
                    ))}
                    <td className="ub-table__col-actions">
                      {isProtectedUserBuilderAccount(u) ? (
                        <span className="ub-muted" title="Protected account">
                          Protected
                        </span>
                      ) : (
                        <div className="ub-table-actions">
                          <GlobalTableActionButton
                            action="assign"
                            disabled={busy}
                            onClick={() => requestAttendanceAssignment(u)}
                          />
                          <GlobalTableActionButton
                            action="edit"
                            disabled={busy}
                            onClick={() => startEdit(u)}
                          />
                          <GlobalTableActionButton
                            action="remove"
                            disabled={busy}
                            onClick={() => requestUserRemoval(u)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            label="Users pagination"
          />
        }
      />

      <GlobalModal
        open={Boolean(userPendingRemoval)}
        onClose={() => !removingUser && cancelUserRemoval()}
        title="Remove user from directory?"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
              flexWrap: "wrap"
            }}
          >
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={cancelUserRemoval}
              disabled={removingUser}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="danger"
              onClick={confirmUserRemoval}
              loading={removingUser}
            >
              Remove user
            </GlobalButton>
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          Remove <strong>{userPendingRemoval?.name}</strong> (
          {userPendingRemoval?.email}) from the directory? Their account will be
          deactivated (inactive) and they will no longer appear in this list or be
          able to sign in.
        </p>
      </GlobalModal>

      <UserAttendanceAssignModal
        open={Boolean(userPendingAssignment)}
        user={userPendingAssignment}
        shifts={attendanceShifts}
        locations={attendanceLocations}
        busy={assigningUser}
        onClose={cancelAttendanceAssignment}
        onSave={confirmAttendanceAssignment}
      />
    </div>
  );
};

export default UserDirectorySection;
